/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import { makeStyles } from '@material-ui/core';
import {
    arrayOf,
    bool, func, number, object, shape, string,
} from 'prop-types';
import { useSelector } from 'react-redux';
import { getPriceArrays } from '../../../../../../../../helpers/DynamicPricing/getPriceArrays';
import PriceRange from './PriceRange';
import { getFeatureFlag } from '../../../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';

const useStyles = makeStyles(() => ({
    container: {
        position: 'relative',

    },
    wrapperLeft: {
        display: 'flex',
        justifyContent: 'flex-end',
        paddingRight: '16px',
        '& $dialog': {
            left: 'unset',
            right: '16px',
            '@media screen and (max-width: 440px)': {
                width: '100%',
            },
        },
    },
    dialog: {
        display: 'flex',
        justifyContent: 'center',
        width: '398px',
        '@media screen and (max-width: 1024px)': {
            padding: '0px 10px 0px 0px',
        },
        '@media screen and (max-width: 400px)': {
            width: '100%',
            padding: '0px 5px 0px 15px',
        },
    },
    dialogOpen: {
        opacity: '1',
        visibility: 'visible',
    },
    dialogClose: {
        opacity: '0',
        visibility: 'hidden',
    },
    dialogWrapper: {
        background: 'white',
        borderRadius: '5px',
        boxShadow: '0px 4px 8px 0px #0000004D',
        margin: '0 auto',
        paddingBottom: '4px',
        minHeight: '250px',
        height: 'max-content',
    },
    dialogHeader: {
        justifyContent: 'space-between',
        width: '100%',
        gridTemplateColumns: 'calc(100% - 16px) 16px',
        display: 'grid',
        padding: '9px 12.5px 9px 20px',
        backgroundColor: '#E9E9E9',
        alignItems: 'center',
        borderRadius: '5px',
        borderBottomRightRadius: '0px',
        borderBottomLeftRadius: '0px',
    },
    closeIcon: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        cursor: 'pointer',
    },
    dialogContent: {
        padding: '16px 20px 12px',
        gridTemplateColumns: '50%  auto',
        display: 'grid',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& div': {
            fontSize: '16px',
            lineHeight: '26px',
            letterSpacing: '0.5px',
            '@media screen and (max-width: 400px)': {
                fontSize: '14px',
            },
            '@media screen and (max-width: 362px)': {
                fontSize: '12px',
            },
        },
    },
    title: {
        fontSize: '14px',
        lineHeight: '26px',
        letterSpacing: '0.5px',
        fontFamily: 'var(--comp-base-font-family-default, Lato , Helvetica, Arial, sans-serif)',
        width: 'auto',
        fontWeight: 700,
    },
    dialogMiddle: {
        borderTop: '1px solid #C4C4C4',
        margin: '0px 20px 12px',
        paddingTop: '12px',
        gridTemplateColumns: '50%  auto',
        display: 'grid',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: 'auto',
        '& div': {
            fontSize: '16px',
            lineHeight: '26px',
            letterSpacing: '0.5px',
            fontFamily: 'museo-sans, "Area Variable", LatoBold',
            fontWeight: '700',
            '@media screen and (max-width: 400px)': {
                fontSize: '14px',
            },
            '@media screen and (max-width: 362px)': {
                fontSize: '12px',
            },
        },
    },
    dialogBottom: {
        fontSize: '14px',
        borderTop: '1px solid #C4C4C4',
        lineHeight: '20px',
        letterSpacing: '0.5px',
        margin: '0px 20px 12px',
        display: 'flex',
        width: 'auto',
        paddingTop: '12px',
    },
    priceContent: {
        display: 'flex',
        justifyContent: 'flex-end',
        textAlign: 'end',
    },
    priceContainer: {
        display: 'inline-block',
        fontSize: 'var(--fontSizes-fontText3, 14px)',
        lineHeight: 'var(--lineHeights-lineText3, 19px)',
        color: 'var(--ENT-gray-900, #000)',
        fontWeight: '500',
        letterSpacing: '0.14px',
        paddingTop: '6px',
        '@media screen and (max-width: 600px)': {
            fontSize: 'var(--fontSizes-fontText4, 12px);',
            lineHeight: 'var(--lineHeights-lineText4, 17px)',
        },
        '& span': {
            display: 'inline-block',
        },
        '& s': {
            display: 'flex',
            paddingBottom: '4px',
        },
    },
    serviceFee: {
        marginLeft: '2px',
    },
    serviceFeeContainer: {
        display: 'inline-block',
    },
    priceAddOnContainer: {
        '& span': {
            fontSize: '16px',
            color: '#434343 !important',
        },
    },
    priceRange: {
        paddingBottom: '4px',
    },
}));

const PriceBreakdownDetail = ({
    priceMessaging, productDeliveryType, dynamicPriceData, priceRangeLayout,
    skuPriceRange,
}) => {
    const classes = useStyles();
    const getDetails = () => priceMessaging?.[productDeliveryType.toLowerCase()] || {};
    const caPriceTransparencyFlag = useSelector(getFeatureFlag('is-gpt-price-transparency-enabled'));
    const summaryModal = getDetails().summary_modal || {};

    const getPrice = (type = 'total', serviceType = '') => {
        if (type === 'delivery') {
            const totalPrices = [];
            dynamicPriceData?.prices?.forEach((item) => {
                if (item.type === 'retail') {
                    const totalPrice = parseFloat(item.value) + (item?.charges?.[0]?.amount ? parseFloat(item.charges?.[0]?.amount) : '');
                    totalPrices.push({
                        type: item.type,
                        value: parseFloat(totalPrice).toFixed(2),
                    });
                } else {
                    const totalPrice = parseFloat(item.value) + (item?.charges?.[0]?.amount ? parseFloat(item.charges?.[0]?.amount) : '');
                    totalPrices.push({
                        type: item.type,
                        value: parseFloat(totalPrice).toFixed(2),
                    });
                }
            });
            return getPriceArrays(totalPrices);
        }
        if (type === 'total') {
            // to avoid updating reference
            const totalPrices = [];
            dynamicPriceData?.prices.forEach((item) => {
                if (item.type === 'retail') {
                    totalPrices.push({
                        type: item.type,
                        value: item.total,
                    });
                } else {
                    totalPrices.push({
                        type: item.type,
                        value: item.total,
                    });
                }
            });
            return getPriceArrays(totalPrices);
        }
        if (type === 'item_price') {
            return getPriceArrays(dynamicPriceData?.prices);
        }
        if (type === 'charges') {
            const chargesPrices = [];
            dynamicPriceData?.prices.forEach((item) => {
                (item?.charges || []).forEach((charge) => {
                    if (charge?.label?.toLowerCase() === serviceType?.toLowerCase()) {
                        if (item.type === 'retail') {
                            chargesPrices.push({
                                charges: item.charges,
                                type: item.type,
                                value: item.charges[0].amount,
                            });
                        } else {
                            chargesPrices.push({
                                charges: item.charges,
                                type: item.type,
                                value: item.charges[0].amount,
                            });
                        }
                    }
                });
            });
            return getPriceArrays(chargesPrices);
        }
        return {};
    };

    const priceDetails = () => {
        let itemPrice = null;
        let serviceCharges = null;
        let valueOverride = '';
        (summaryModal?.summary_labels || []).forEach((item) => {
            if (item.label_key === 'item_price') {
                itemPrice = getPrice('delivery', item.label_key);
            }
            if (item.label_key === 'service fee') {
                serviceCharges = getPrice('charges', item.label_key);
            }

            if (item.value_override) {
                valueOverride = 'plus shipping';
            }
        });
        return (
            (itemPrice || serviceCharges) && (
                <span className={classes.priceContainer}>
                    {serviceCharges && (
                        <div>
                            <div className={classes.priceRange}>
                                <PriceRange
                                    priceRangeLayout=""
                                    displayPrice
                                    skuPriceRange={skuPriceRange}
                                    collectionRestructureEnabled={false}
                                    collectionPageHierarchyEnabled={false}
                                    forceItemPriceOnly
                                    noFromText
                                />
                            </div>
                            <PriceRange
                                priceRangeLayout=""
                                displayPrice
                                skuPriceRange={itemPrice}
                                collectionRestructureEnabled={false}
                                collectionPageHierarchyEnabled={false}
                                forceItemPriceOnly
                                noFromText
                                showSinglePrice
                            />
                            <span className={classes.serviceFee}>
                                with delivery fee
                            </span>
                        </div>
                    )}
                    {
                        valueOverride && (
                            <div>
                                <PriceRange
                                    priceRangeLayout=""
                                    displayPrice
                                    skuPriceRange={caPriceTransparencyFlag?.[productDeliveryType?.toLowerCase()]?.withDeliveryCharges ? itemPrice : skuPriceRange}
                                    collectionRestructureEnabled={false}
                                    collectionPageHierarchyEnabled={false}
                                    forceItemPriceOnly
                                    noFromText
                                />

                                <span className={`${classes.underline} ${classes.serviceFee}`}>{valueOverride}</span>
                            </div>
                        )
                    }
                </span>
            )
        );
    };

    return (
        priceDetails()
    );
};

PriceBreakdownDetail.propTypes = {
    dynamicPriceData: object.isRequired,
    productDeliveryType: string.isRequired,
    priceRangeLayout: object.isRequired,
    openPriceDialog: func.isRequired,
    priceMessaging: object.isRequired,
    addOnPrice: bool,
    skuPriceRange: shape({
        retail: arrayOf(shape({
            value: number,
        })),
        sale: arrayOf(shape({
            value: number,
        })),
    }),
};
PriceBreakdownDetail.defaultProps = {
    addOnPrice: false,
    skuPriceRange: {},
};

export default PriceBreakdownDetail;
